import React, { Suspense, useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { KpiBlock } from '../blocks/KpiBlock';
import { TableBlock } from '../blocks/TableBlock';
import { ChartBlock } from '../blocks/ChartBlock';
import { HeatmapBlock } from '../blocks/HeatmapBlock';
import { FunnelBlock } from '../blocks/FunnelBlock';
import { TreemapBlock } from '../blocks/TreemapBlock';
import { TextBlock } from '../blocks/TextBlock';
import type { HeatmapCell, KpiBlockData, TreemapNode } from '../types';
import type { ColumnDefinition } from '../../../../../services/claudeQueryService';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import type { DatasetMap, ViewPanel } from './types';
import { applyTransforms, computeKpi, matchesThreshold } from './transforms';
import { formatDateLabel, formatKpiValue } from './format';
import type { ViewTheme } from './viewTheme';

const VegaPanel = React.lazy(() => import('./VegaPanel'));

interface PanelHostProps {
    panel: ViewPanel;
    datasets: DatasetMap;
    theme: ViewTheme;
    /** Høyde for innholdet (uten panelhode), piksler */
    height: number;
}

const FieldError: React.FC<{ message: string }> = ({ message }) => (
    <div className="flex items-start gap-2 p-3 text-xs rounded bg-yellow-50 border border-yellow-200 text-yellow-800">
        <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
        <span>{message}</span>
    </div>
);

/** Rader for panelet etter transform, eller null hvis datasettet mangler. */
function usePanelRows(panel: ViewPanel, datasets: DatasetMap) {
    return useMemo(() => {
        const ds = panel.dataset ? datasets[panel.dataset] : undefined;
        if (!ds) return null;
        return { rows: applyTransforms(ds.rows, panel.transform), columns: ds.columns };
    }, [panel.dataset, panel.transform, datasets]);
}

const missingFields = (rows: Record<string, any>[], fields: (string | undefined)[]): string[] => {
    if (rows.length === 0) return [];
    const keys = new Set(Object.keys(rows[0]));
    return fields.filter((f): f is string => !!f && !keys.has(f));
};

export const PanelHost: React.FC<PanelHostProps> = ({ panel, datasets, theme, height }) => {
    const { t } = useTranslation();
    const data = usePanelRows(panel, datasets);
    const cfg = panel.config ?? {};

    if (panel.kind === 'text')
        return <div className="overflow-auto h-full"><TextBlock content={panel.content ?? ''} /></div>;

    if (!data)
        return <FieldError message={t('manager.modules.claudeQuery.view.missingDataset', { '0': panel.dataset ?? '' }, `Datasettet «${panel.dataset ?? ''}» mangler.`)} />;

    const { rows, columns } = data;
    const fieldWarning = (fields: (string | undefined)[]) => {
        const missing = missingFields(rows, fields);
        return missing.length > 0
            ? <FieldError message={t('manager.modules.claudeQuery.view.missingFields', { '0': missing.join(', ') }, `Visningen bruker felt som ikke finnes i dataene: ${missing.join(', ')}. Be Compass tilpasse visningen.`)} />
            : null;
    };

    switch (panel.kind) {
        case 'kpi': {
            const items = (cfg.items ?? []) as any[];
            const kpi: KpiBlockData = {
                items: items.map(item => {
                    const source = item.dataset && datasets[item.dataset] ? datasets[item.dataset].rows : rows;
                    const value = computeKpi(source, item);
                    // Andeler uten format fikk ikke %-tegn («Andel av alle plukk 56,4»)
                    const format = item.format ?? (/andel|prosent|%/i.test(item.label ?? '') ? 'percent' : undefined);
                    const color = matchesThreshold(value, item.thresholds?.bad) ? 'red'
                        : matchesThreshold(value, item.thresholds?.good) ? 'green' : 'blue';
                    return { label: item.label, value: formatKpiValue(value, format), color };
                })
            };
            return <div className="h-full overflow-auto">{fieldWarning(items.filter(i => !i.dataset).map(i => (i.aggregate === 'count' ? undefined : i.field)))}<KpiBlock data={kpi} /></div>;
        }

        case 'table': {
            const wanted: string[] | undefined = cfg.columns;
            const cols: ColumnDefinition[] = wanted?.length
                ? wanted.map(name => columns.find(c => c.name === name) ?? { name, displayName: name, dataType: 'string', sortable: true, filterable: true } as ColumnDefinition)
                : rows.length > 0 && columns.length === 0
                    ? Object.keys(rows[0]).map(name => ({ name, displayName: name, dataType: typeof rows[0][name] === 'number' ? 'number' : 'string', sortable: true, filterable: true } as ColumnDefinition))
                    : columns;
            const rules = (cfg.highlightRules ?? []) as { columnName: string; condition: string; value: number; color?: string }[];
            const cellClassName = rules.length === 0 ? undefined : (row: Record<string, any>, column: ColumnDefinition) => {
                const rule = rules.find(r => r.columnName === column.name &&
                    matchesThreshold(typeof row[column.name] === 'number' ? row[column.name] : Number(row[column.name]),
                        `${({ gt: '>', gte: '>=', lt: '<', lte: '<=', eq: '=', neq: '!=' } as Record<string, string>)[r.condition] ?? '='} ${r.value}`));
                if (!rule) return undefined;
                // Temafarger (lys/grå/mørk) fra --color-chart-*
                return rule.color === 'bad' ? 'text-[color:var(--color-chart-bad)] font-semibold'
                    : rule.color === 'good' ? 'text-[color:var(--color-chart-good)] font-semibold'
                    : rule.color === 'warning' ? 'text-[color:var(--color-chart-warning)] font-semibold' : 'font-semibold';
            };
            return (
                <div className="h-full overflow-auto">
                    {fieldWarning(wanted ?? [])}
                    <TableBlock
                        data={{ title: undefined, columns: cols, rows, totalRowCount: rows.length, exportable: true, exportFormats: ['csv', 'xlsx'] } as any}
                        initialSort={cfg.defaultSort ? { column: cfg.defaultSort.columnName, direction: cfg.defaultSort.direction ?? 'asc' } : undefined}
                        cellClassName={cellClassName}
                    />
                </div>
            );
        }

        case 'chart': {
            const xKey: string | undefined = cfg.xAxis?.dataKey;
            const series = (cfg.series ?? []) as { name?: string; dataKey: string }[];
            const keys = [xKey, ...series.map(s => s.dataKey)];
            const warning = fieldWarning(keys);
            if (warning) return warning;
            const isDate = columns.find(c => c.name === xKey)?.dataType === 'date';
            // Bare feltene grafen bruker (ChartBlock tolker ellers alle tallkolonner som serier)
            const points = rows.map(r => {
                const p: Record<string, any> = {};
                if (xKey) p[xKey] = isDate ? formatDateLabel(r[xKey]) : r[xKey];
                for (const s of series) p[s.dataKey] = typeof r[s.dataKey] === 'number' ? r[s.dataKey] : Number(r[s.dataKey] ?? 0);
                return p;
            });
            return (
                <div className="h-full overflow-auto">
                <ChartBlock
                    data={{
                        chartType: cfg.chartType ?? 'bar',
                        title: '',
                        series: series.map(s => ({ name: s.name ?? s.dataKey, dataKey: s.dataKey, data: points })),
                        xAxis: xKey ? { dataKey: xKey, label: cfg.xAxis?.label } : undefined,
                        yAxis: series[0] ? { dataKey: series[0].dataKey, label: cfg.yAxis?.label } : undefined
                    } as any}
                    palette={theme.palette}
                    embedded
                    // Litt luft: panelhodet med undertittel er høyere enn PANEL_HEADER, ellers fikk panelet rullefelt
                    height={Math.max(120, height - 14)}
                    currentVizType={cfg.chartType}
                />
                </div>
            );
        }

        case 'heatmap': {
            const warning = fieldWarning([cfg.codeField, cfg.valueField, cfg.rowField, cfg.colField]);
            if (warning) return warning;
            const cells: HeatmapCell[] = rows.map(r => ({
                code: String(r[cfg.codeField] ?? ''),
                label: cfg.labelField ? String(r[cfg.labelField] ?? '') : undefined,
                value: Number(r[cfg.valueField] ?? 0),
                row: cfg.rowField ? String(r[cfg.rowField] ?? '') : undefined,
                col: cfg.colField ? String(r[cfg.colField] ?? '') : undefined
            }));
            return <div className="h-full overflow-auto"><HeatmapBlock data={{ title: undefined, cells, colorScale: cfg.colorScale, valueLabel: cfg.valueLabel }} /></div>;
        }

        case 'funnel': {
            const warning = fieldWarning([cfg.labelField, cfg.valueField]);
            if (warning) return warning;
            const steps = rows.map(r => ({ label: String(r[cfg.labelField] ?? ''), value: Number(r[cfg.valueField] ?? 0) }));
            return <div className="h-full overflow-auto"><FunnelBlock data={{ title: undefined, steps }} /></div>;
        }

        case 'treemap': {
            const warning = fieldWarning([cfg.nameField, cfg.valueField, cfg.parentField]);
            if (warning) return warning;
            let nodes: TreemapNode[];
            if (cfg.parentField) {
                const groups = new Map<string, TreemapNode>();
                for (const r of rows) {
                    const parent = String(r[cfg.parentField] ?? '');
                    const node = groups.get(parent) ?? { name: parent, value: 0, children: [] };
                    const value = Number(r[cfg.valueField] ?? 0);
                    node.value += value;
                    node.children!.push({ name: String(r[cfg.nameField] ?? ''), value });
                    groups.set(parent, node);
                }
                nodes = Array.from(groups.values());
            } else {
                nodes = rows.map(r => ({ name: String(r[cfg.nameField] ?? ''), value: Number(r[cfg.valueField] ?? 0) }));
            }
            return <div className="h-full overflow-auto"><TreemapBlock data={{ title: undefined, data: nodes, valueLabel: cfg.valueLabel }} /></div>;
        }

        case 'vega-lite':
            return (
                <Suspense fallback={<div className="text-xs text-gray-400 p-3">{t('common.loading', {}, 'Laster…')}</div>}>
                    <VegaPanel panel={panel} rows={rows} columns={columns} theme={theme} height={height} />
                </Suspense>
            );

        default:
            return <FieldError message={t('manager.modules.claudeQuery.view.unknownPanel', { '0': panel.kind }, `Ukjent paneltype «${panel.kind}».`)} />;
    }
};

